import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch, apiMutate } from "@/lib/api";
import { shouldRetryQuery } from "@/lib/query-retry";

export interface ChangelogEntry {
  id: number;
  title: string;
  description: string;
  category: string;
  version: string | null;
  is_internal: number;
  created_at: string;
  published_at: string | null;
  /** #583: page this entry relates to; null = global. Filtered server-side by viewer access. */
  page_path?: string | null;
}

export interface FeatureRequest {
  id: number;
  user_id: string;
  user_name: string;
  title: string;
  description: string;
  status: string;
  vote_count: number;
  user_voted: boolean;
  created_at: string;
  updated_at: string;
  /**
   * #926: the idea's review, sent to admins only and absent until
   * migrate-926 is applied. An approved idea carries the issue it filed.
   */
  review_decision?: "approved" | "rejected" | null;
  review_by?: string | null;
  review_at?: string | null;
  review_note?: string | null;
  gh_issue_number?: number | null;
  gh_issue_url?: string | null;
  gh_issue_state?: "open" | "closed" | null;
  shipped_at?: string | null;
}

export function useChangelog() {
  return useQuery({
    queryKey: ["changelog"],
    queryFn: () => apiFetch<ChangelogEntry[]>("/api/updates/changelog"),
  });
}

/** #583 phase 2: admin-triggered GitHub → draft-entries sync. */
export function useSyncChangelogFromGitHub() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiMutate<{
        ok: boolean;
        created?: number;
        deduped?: number;
        published?: Array<{ pr: number; title: string; page_path: string }>;
        drafts?: Array<{ pr: number; title: string }>;
        skipped?: string;
        error?: string;
      }>(
        "/api/updates/changelog/sync-github",
        "POST",
        {},
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["changelog"] }),
  });
}

export function useCreateChangelogEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (entry: { title: string; description: string; category?: string; version?: string; is_internal?: boolean; page_path?: string | null }) =>
      apiMutate<{ id: number }>("/api/updates/changelog", "POST", entry),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["changelog"] });
    },
  });
}

// Admin-only: flip an entry's internal (admin-only) visibility. Dev/infra
// entries marked internal vanish for non-admins; admins still see them.
export function useSetChangelogInternal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, is_internal }: { id: number; is_internal: boolean }) =>
      apiMutate<{ ok: boolean; is_internal: boolean }>(`/api/updates/changelog/${id}`, "PUT", { is_internal }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["changelog"] });
    },
  });
}

export function useDeleteChangelogEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      apiMutate<{ ok: boolean }>(`/api/updates/changelog/${id}`, "DELETE"),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["changelog"] });
    },
  });
}

export function useFeatureRequests(sort?: string) {
  return useQuery({
    queryKey: ["feature-requests", sort],
    queryFn: () =>
      apiFetch<FeatureRequest[]>("/api/updates/requests", sort ? { sort } : undefined),
  });
}

export function useCreateFeatureRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (request: { title: string; description: string }) =>
      apiMutate<{ id: number }>("/api/updates/requests", "POST", request),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["feature-requests"] });
    },
  });
}

export function useToggleVote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (requestId: number) =>
      apiMutate<{ voted: boolean }>(`/api/updates/requests/${requestId}/vote`, "POST"),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["feature-requests"] });
    },
  });
}

export function useUpdateRequestStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) =>
      apiMutate<{ ok: boolean }>(`/api/updates/requests/${id}/status`, "PUT", { status }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["feature-requests"] });
    },
  });
}

/** #926: approve files a GitHub issue on crosbe-ai and sets the idea to planned (admin only). */
export function useApproveIdea() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      apiMutate<{ ok: true; status: string; issue: { number: number; url: string }; labels: string[] }>(
        `/api/updates/requests/${id}/approve`,
        "POST",
        {},
      ),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["feature-requests"] });
    },
  });
}

/** #926: reject files nothing and sets the idea to declined (admin only). */
export function useRejectIdea() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: { id: number; note?: string }) =>
      apiMutate<{ ok: true; status: string }>(`/api/updates/requests/${id}/reject`, "POST", note ? { note } : {}),
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ["feature-requests"] });
    },
  });
}

export function useDeleteFeatureRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      apiMutate<{ ok: boolean }>(`/api/updates/requests/${id}`, "DELETE"),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["feature-requests"] });
    },
  });
}

// ─── What's New announce surface (#1285) ──────────────────────

export interface WhatsNewEntry {
  id: number;
  title: string;
  description: string;
  category: string;
  version: string | null;
  page_path: string | null;
  created_at: string;
  published_at: string;
}

export function useWhatsNew() {
  return useQuery({
    queryKey: ["whats-new"],
    queryFn: () => apiFetch<{ entries: WhatsNewEntry[] }>("/api/updates/whats-new"),
    staleTime: 5 * 60_000,
    // announce surface: a failed fetch stays invisible, one resend for a
    // fault and none for a 4xx (#3319).
    retry: shouldRetryQuery,
  });
}

export function useAckWhatsNew() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiMutate<{ ok: boolean }>("/api/updates/whats-new/ack", "POST", {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["whats-new"] });
    },
  });
}

const CHANGELOG_SEEN_KEY = "changelog-last-seen";

function comparableChangelogTimestamp(value: string): string {
  return value.replace("T", " ").replace(/\.\d{3}Z?$/, "").replace(/Z$/, "");
}

export function useHasNewChangelog(): boolean {
  const { data: entries } = useChangelog();

  if (!entries?.length) return false;

  try {
    const latestPublishedAt = entries.reduce<string | null>((latest, entry) => {
      if (!entry.published_at) return latest;
      const publishedAt = comparableChangelogTimestamp(entry.published_at);
      if (latest && publishedAt <= latest) return latest;
      return publishedAt;
    }, null);
    if (!latestPublishedAt) return false;
    const lastSeen = localStorage.getItem(CHANGELOG_SEEN_KEY);
    if (!lastSeen) return true;
    return latestPublishedAt > comparableChangelogTimestamp(lastSeen);
  } catch {
    return false;
  }
}

export function markChangelogSeen() {
  try {
    localStorage.setItem(CHANGELOG_SEEN_KEY, new Date().toISOString().slice(0, 19).replace("T", " "));
  } catch {
    // ignore
  }
}
